import React, { useMemo } from 'react';
import OWRadioGroup from '../components/ow_radio_group/OWRadioGroup';
import {
	changeFilterValueToArrayOfIds,
	fetchWithDifferntSearchName,
	getEndDateFieldName,
	getStartDateFieldName,
	isSupplierPayingCustomer,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../utils/DataAccessUtils';
import OWAsyncTreeSelect from '../components/ow_async_tree_select/OWAsyncTreeSelect';
import {
	FILTER_CONFIG_NAMES,
	FILTER_FIELD_TYPE,
	FILTER_SEPARATOR,
	FILTER_VALUE_TYPE,
} from '../utils/DataConstants';
import { AreaTreeSelect } from '../components/area_tree_select';
import ProblemTypeTreeSelect from '../components/problem_type_tree_select';
import { isUserManagedBySupplier } from '../utils/AuthUtils';
import {
	decodeUTCAdjustedDate,
	encodeUTCAdjustedDate,
	formatDateForParam,
	getContactName,
} from '../utils/DataFormatterUtils';
import moment from 'moment';
import OWDateRangePicker from '../components/ow_date_range_picker/OWDateRangePicker';
import {
	displayStatusMap,
	statusMap,
} from '../components/WorkOrderTable/config/pipelineFilters/pipelineFilters';
import AssetFilterRowDisplay from './AssetFilterRowDisplay';
import OWAsyncSelect from '../components/ow_async_select/OWAsyncSelect';
import { UserName } from '../components/name_component/user/UserNameComponent';
import OWSelect from '../components/ow_radio_group/OWSelect';

export const useDashboardFilterConfiguration = ({ view }) => {
	return useMemo(
		() => [
			{
				label: 'Status',
				fieldName: 'statuses',
				type: FILTER_FIELD_TYPE.PIPE_FILTER,
				values: nullSafeGetOrElse('filterValue.statuses', view, []),
				mode: 'multiple',
				hideFilter: true,
				keyAccessor: (key) => key,
				valueAccessor: (value) => value,
				targetCollectionName: 'workOrdersAdvancedFilters',
				renderItem: (value) => statusMap[value],
				sortBy: { sort_by: 'name', order: 'ascend' },
			},
			{
				label: 'Display Status',
				fieldName: 'displayStatuses',
				type: FILTER_FIELD_TYPE.PIPE_FILTER,
				values: nullSafeGetOrElse('filterValue.displayStatuses', view, []),
				mode: 'multiple',
				hideFilter: true,
				keyAccessor: (key) => key,
				valueAccessor: (value) => value,
				targetCollectionName: 'workOrdersAdvancedFilters',
				renderItem: (value) => displayStatusMap[value],
				sortBy: { sort_by: 'name', order: 'ascend' },
			},
			...(nullSafeGet('filterValue.search', view)
				? [
						{
							label: 'Search',
							fieldName: 'search',
							type: FILTER_FIELD_TYPE.PIPE_FILTER,
							values: [nullSafeGetOrElse('filterValue.search', view, '')],
							mode: 'multiple',
							hideFilter: true,
							keyAccessor: (key) => key,
							valueAccessor: (value) => value,
							targetCollectionName: 'workOrdersAdvancedFilters',
							renderItem: (value) => `"${value}"`,
							sortBy: { sort_by: 'name', order: 'ascend' },
						},
				  ]
				: []),
		],
		[view]
	);
};
export const useBuyerFilterConfiguration = ({
	updateFilters,
	fetchBrands,
	fetchMultipleBrands,
	brands,
	brandAvailable,
}) => {
	return useMemo(
		() => [
			...(brandAvailable
				? [
						{
							label: 'Brands',
							fieldName: 'brandIds',
							mode: 'multiple',
							stateSlice: brands,
							filtersValueAccessor: (filtersStateSlice) => {
								const brandIds = nullSafeGet(`brandIds`, filtersStateSlice);
								return changeFilterValueToArrayOfIds(brandIds);
							},
							keyAccessor: (brand) => brand.id,
							valueAccessor: (brand) => brand.id,
							labelAccessor: (brand) => brand.brandName,
							handleChange: (ids) => {
								updateFilters({
									brandIds: ids.join(',') || undefined,
								});
							},
							targetCollectionName: 'workOrdersAdvancedFilters',
							fetch: fetchBrands,
							fetchMultiple: (ids, targetCollectionName) =>
								fetchMultipleBrands(ids, targetCollectionName),
							renderItem: (brand) => brand.brandName,
							renderRecord: (brand) => brand.brandName,
							component: OWAsyncTreeSelect,
						},
				  ]
				: []),
		],
		[updateFilters, fetchBrands, fetchMultipleBrands, brands, brandAvailable]
	);
};

export const useSupplierFilterConfiguration = ({
	updateFilters,
	fieldTechs,
	fetchFieldTechs,
	fetchMultipleFieldTechs,
	spendCategories,
	fetchSpendCategories,
	fetchMultipleSpendCategories,
	currentUser,
	contractorPrivateNetworks,
	fetchContractors,
	contractorClients,
	fetchContractorClients,
	fetchMultipleContractorClients,
	contractorLocations,
	fetchContractorLocations,
	fetchMultipleContractorLocations,
}) => {
	return useMemo(
		() => [
			{
				label: 'Clients',
				fieldName: 'contractorClientId',
				stateSlice: contractorClients,
				filtersValueAccessor: (filtersStateSlice) =>
					nullSafeGet(`contractorClientId`, filtersStateSlice),
				keyAccessor: (cc) => cc.id,
				valueAccessor: (cc) => cc.id,
				handleChange: (contractorClientId) => {
					updateFilters({ contractorClientId });
				},
				targetCollectionName: 'workOrdersAdvancedFilters',
				fetch: fetchContractorClients,
				fetchMultiple: fetchMultipleContractorClients,
				renderItem: (cc) => cc.name,
				sortBy: { sort_by: 'name', order: 'ascend' },
			},
			// {
			// 	label: 'Locations',
			// 	fieldName: 'contractorLocationId',
			// 	stateSlice: contractorLocations,
			// 	filtersValueAccessor: (filtersStateSlice) =>
			// 		nullSafeGet(`contractorLocationId`, filtersStateSlice),
			// 	keyAccessor: (cc) => cc.id,
			// 	valueAccessor: (cc) => cc.id,
			// 	handleChange: (contractorLocationId) => {
			// 		updateFilters({ contractorLocationId });
			// 	},
			// 	targetCollectionName: 'workOrdersAdvancedFilters',
			// 	fetch: fetchContractorLocations,
			// 	fetchMultiple: fetchMultipleContractorLocations,
			// 	renderItem: (cc) => cc.name,
			// 	sortBy: { sort_by: 'name', order: 'ascend' },
			// },
			{
				label: 'Technician',
				fieldName: 'fieldTechnicianEmail',
				stateSlice: fieldTechs,
				filtersValueAccessor: (filtersStateSlice) =>
					nullSafeGet(`fieldTechnicianEmail`, filtersStateSlice),
				keyAccessor: (fieldTech) => fieldTech.email,
				valueAccessor: (fieldTech) => fieldTech.email,
				handleChange: (technicianEmail) => {
					updateFilters({ fieldTechnicianEmail: technicianEmail });
				},
				targetCollectionName: 'workOrdersAdvancedFilters',
				fetch: fetchFieldTechs,
				fetchMultiple: fetchMultipleFieldTechs,
				renderItem: (fieldTech) => fieldTech.name,
				sortBy: { sort_by: 'name', order: 'ascend' },
			},
			{
				label: 'Trade',
				fieldName: 'spendCategoryIds',
				mode: 'multiple',
				stateSlice: spendCategories,
				filtersValueAccessor: (filtersStateSlice) => {
					const ids = nullSafeGet(`spendCategoryIds`, filtersStateSlice);
					return changeFilterValueToArrayOfIds(ids);
				},
				keyAccessor: (spendCategory) => spendCategory.id,
				valueAccessor: (spendCategory) => spendCategory.id,
				handleChange: (ids) => updateFilters({ spendCategoryIds: ids.join(',') || undefined }),
				targetCollectionName: 'workOrdersAdvancedFilters',
				fetch: fetchSpendCategories,
				fetchMultiple: fetchMultipleSpendCategories,
				renderItem: (spendCategory) => spendCategory.name,
				sortBy: { sort_by: 'name', order: 'ascend' },
				component: OWAsyncTreeSelect,
			},
			...(isSupplierPayingCustomer(currentUser)
				? [
						{
							label: 'Sub Contractors',
							fieldName: FILTER_CONFIG_NAMES.SUB_CONTRACTORS,
							mode: 'multiple',
							stateSlice: contractorPrivateNetworks,
							filtersValueAccessor: (filtersStateSlice) =>
								changeFilterValueToArrayOfIds(
									nullSafeGet(`subContractorFacilityIds`, filtersStateSlice)
								),
							keyAccessor: (item) => nullSafeGet('supplierFacility.id', item),
							valueAccessor: (item) => nullSafeGet('supplierFacility.id', item),
							handleChange: (ids) => {
								updateFilters({
									subContractorFacilityIds: ids.join(',') || undefined,
								});
							},
							targetCollectionName: 'workOrdersAdvancedFilters',
							fetch: fetchContractors,
							fetchMultiple: () => {},
							renderItem: (item) =>
								nullSafeGet('supplierFacility.name', item) || nullSafeGet('name', item),
							sortBy: { sort_by: 'name', order: 'ascend' },
						},
				  ]
				: []),
		],
		[
			updateFilters,
			fieldTechs,
			fetchFieldTechs,
			fetchMultipleFieldTechs,
			spendCategories,
			fetchSpendCategories,
			fetchMultipleSpendCategories,
			currentUser,
			contractorPrivateNetworks,
			fetchContractors,
			contractorClients,
			fetchContractorClients,
			fetchMultipleContractorClients,
			contractorLocations,
			fetchContractorLocations,
			fetchMultipleContractorLocations,
		]
	);
};

export const useCommonFilterConfiguration = ({
	locations,
	fetchLocations,
	areas,
	fetchAreasDistinct,
	workOrderPriorities,
	fetchWorkOrderPriorities,
	assets,
	fetchAssets,
	fetchMultipleAssets,
	assetTypes,
	fetchAssetTypes,
	fetchMultipleAssetTypes,
	planned_maintenance_schedules,
	fetchPMSchedule,
	fetchMultiplePMSchedule,
	problemTypes,
	fetchProblemTypes,
	currentUser,
	supplierFacilities,
	fetchSupplierFacilitiesInPrivateNetwork,
	resolutionTypes,
	fetchResolutionTypes,
	fetchMultipleResolutionTypes,
	staleDays,
	updateFilters,
	users,
	fetchUsers,
	fetchMultipleUsers,
}) => {
	return useMemo(
		() => [
			{
				label: 'Location',
				fieldName: 'locationIds',
				mode: 'multiple',
				stateSlice: locations,
				hideFilter: true,
				filtersValueAccessor: (activeFilters) => {
					const locationIds = nullSafeGet(`locationIds`, activeFilters);
					const finalIds = nullSafeGetOrElse(`regionIds`, activeFilters, locationIds);
					return changeFilterValueToArrayOfIds(finalIds);
				},
				keyAccessor: (loc) => loc.id,
				valueAccessor: (loc) => loc.id,
				handleChange: (ids) => {
					updateFilters({
						locationIds: ids.join(',') || undefined,
						regionIds: undefined,
					});
				},
				targetCollectionName: 'workOrdersAdvancedFilters',
				fetch: fetchLocations, //This value is undefined, why have it?
				fetchMultiple: () => {},
				renderItem: (loc) => loc.name,
				sortBy: { sort_by: 'name', order: 'ascend' },
			},
			{
				label: 'Area',
				fieldName: FILTER_CONFIG_NAMES.AREA,
				mode: 'multiple',
				stateSlice: areas,
				valueType: FILTER_VALUE_TYPE.STRING,
				separator: FILTER_SEPARATOR.TILDE,
				filtersValueAccessor: (filtersStateSlice) => {
					const areaNames = nullSafeGet(FILTER_CONFIG_NAMES.AREA, filtersStateSlice);
					return `${areaNames || ''}`.split(FILTER_SEPARATOR.TILDE).filter((_) => !!_);
				},
				keyAccessor: (area) => area.hierarchyName,
				valueAccessor: (area) => area.hierarchyName,
				labelAccessor: (area) => area.hierarchyName,
				handleChange: (areaNames) => {
					updateFilters({
						[FILTER_CONFIG_NAMES.AREA]: areaNames.join(FILTER_SEPARATOR.TILDE) || undefined,
					});
				},
				targetCollectionName: 'workOrdersAdvancedFilters',
				fetch: fetchWithDifferntSearchName(fetchAreasDistinct, 'search'),
				fetchMultiple: () => {},
				renderItem: (area) => area.hierarchyName,
				sortBy: { sort_by: 'name', order: 'ascend' },
				component: AreaTreeSelect,
				isSpecial: true,
			},
			{
				label: 'Priority',
				fieldName: 'woPriorityIds',
				mode: 'multiple',
				stateSlice: workOrderPriorities,
				filtersValueAccessor: (filtersStateSlice) => {
					const woPriorityIds = nullSafeGet(`woPriorityIds`, filtersStateSlice);
					return changeFilterValueToArrayOfIds(woPriorityIds);
				},
				keyAccessor: (workOrderPriority) => workOrderPriority.id,
				valueAccessor: (workOrderPriority) => workOrderPriority.id,
				handleChange: (ids) => {
					updateFilters({
						woPriorityIds: ids.join(',') || undefined,
					});
				},
				targetCollectionName: 'workOrdersAdvancedFilters',
				fetch: fetchWorkOrderPriorities,
				fetchMultiple: () => {},
				renderItem: (workOrderPriority) => workOrderPriority.name,
				component: OWAsyncTreeSelect,
			},
			{
				label: 'Asset',
				fieldName: FILTER_CONFIG_NAMES.ASSET,
				mode: 'multiple',
				stateSlice: assets,
				filtersValueAccessor: (filtersStateSlice) => {
					const assetIds = nullSafeGet(`assetIds`, filtersStateSlice);
					return changeFilterValueToArrayOfIds(assetIds);
				},
				keyAccessor: (asset) => asset.id,
				valueAccessor: (asset) => asset.id,
				handleChange: (ids) => {
					updateFilters({
						assetIds: ids.join(',') || undefined,
					});
				},
				targetCollectionName: 'workOrdersAdvancedFilters',
				fetch: fetchWithDifferntSearchName(fetchAssets, 'search'),
				fetchMultiple: fetchMultipleAssets,
				renderItem: (asset) => <AssetFilterRowDisplay asset={asset} />,
				renderSelectedText: (asset) => asset.name,
				component: OWAsyncSelect,
			},
			{
				label: 'Asset Type',
				fieldName: FILTER_CONFIG_NAMES.ASSET_TYPE,
				mode: 'multiple',
				stateSlice: assetTypes,
				filtersValueAccessor: (filtersStateSlice) => {
					const assetTypeIds = nullSafeGet(`assetTypeIds`, filtersStateSlice);
					return changeFilterValueToArrayOfIds(assetTypeIds);
				},
				keyAccessor: (assetType) => assetType.id,
				valueAccessor: (assetType) => assetType.id,
				handleChange: (ids) => {
					updateFilters({
						assetTypeIds: ids.join(',') || undefined,
					});
				},
				targetCollectionName: 'workOrdersAdvancedFilters',
				fetch: fetchAssetTypes,
				fetchMultiple: fetchMultipleAssetTypes,
				renderItem: (assetType) => assetType.name,
				component: OWAsyncTreeSelect,
			},
			{
				fieldName: 'isCapex',
				type: 'radioGroup',
				items: [
					{ label: 'All', value: undefined },
					{ label: 'R&M', value: 'false' },
					{ label: 'CapEx', value: 'true' },
				],
				filtersValueAccessor: (filtersStateSlice) => {
					const val = filtersStateSlice.isCapex;
					return typeof val === 'undefined' ? undefined : `${val}`;
				},
				keyAccessor: (item) => item.value,
				valueAccessor: (item) => item.value,
				handleChange: (isCapex) => {
					const val = isCapex ? isCapex === 'true' : undefined;
					updateFilters({ isCapex: val });
				},
				renderRecord: (item) => item.label,
				component: OWRadioGroup,
				info: [
					{
						title: 'Repair and Maintenance (R&M)',
						description: 'Costs associated with repair and maintenance of existing assets',
					},
					{
						title: 'Capital Expenditures (CapEx)',
						description: 'Costs associated with buying new assets or upgrading existing assets',
					},
				],
			},
			{
				label: 'PM Schedule',
				fieldName: FILTER_CONFIG_NAMES.PM_SCHEDULE,
				stateSlice: planned_maintenance_schedules,
				filtersValueAccessor: (filtersStateSlice) => {
					const id = nullSafeGet(FILTER_CONFIG_NAMES.PM_SCHEDULE, filtersStateSlice);
					return id;
				},
				hideFilter: true,
				keyAccessor: (item) => item.id,
				valueAccessor: (item) => `${item.id}`,
				handleChange: (id) => {
					updateFilters({
						[FILTER_CONFIG_NAMES.PM_SCHEDULE]: id || undefined,
					});
				},
				targetCollectionName: 'workOrdersAdvancedFilters',
				fetch: fetchPMSchedule,
				fetchMultiple: fetchMultiplePMSchedule,
				renderItem: (item) => item.title,
			},
			{
				label: 'Site Survey Task',
				type: FILTER_FIELD_TYPE.STATIC,
				fieldName: FILTER_CONFIG_NAMES.SITE_SURVEY_TASK,
				stateSlice: planned_maintenance_schedules,
				filtersValueAccessor: (filtersStateSlice) => {
					const id = nullSafeGet(FILTER_CONFIG_NAMES.SITE_SURVEY_TASK, filtersStateSlice);
					return id;
				},
				hideFilter: true,
				keyAccessor: (item) => item.id,
				valueAccessor: (item) => `${item.id}`,
				handleChange: (id) => {
					updateFilters({
						[FILTER_CONFIG_NAMES.SITE_SURVEY_TASK]: id || undefined,
					});
				},
				values: ['Site Survey Task'],
				value: 'Site Survey Task',
				targetCollectionName: 'workOrdersAdvancedFilters',
				fetch: () => {},
				fetchMultiple: () => {},
				renderItem: () => 'Selected',
			},
			{
				label: 'Problem Type',
				mode: 'multiple',
				fieldName: 'problemTypeIds',
				stateSlice: problemTypes,
				filtersValueAccessor: (filtersStateSlice) => {
					const problemTypeIds = nullSafeGet(`problemTypeIds`, filtersStateSlice);
					return changeFilterValueToArrayOfIds(problemTypeIds);
				},
				keyAccessor: (problemType) => problemType.id,
				valueAccessor: (problemType) => problemType.id,
				handleChange: (ids) => {
					updateFilters({
						problemTypeIds: ids.join(',') || undefined,
					});
				},
				targetCollectionName: 'workOrdersAdvancedFilters',
				fetch: fetchProblemTypes,
				fetchMultiple: () => {},
				renderItem: (problemType) => problemType.hierarchyName,
				sortBy: { sort_by: 'name', order: 'ascend' },
				component: ProblemTypeTreeSelect,
			},
			...(isUserManagedBySupplier(currentUser)
				? []
				: [
						{
							label: 'Supplier',
							fieldName: 'supplierFacilityIds',
							mode: 'multiple',
							stateSlice: supplierFacilities,
							filtersValueAccessor: (filtersStateSlice) =>
								changeFilterValueToArrayOfIds(
									nullSafeGet(`supplierFacilityIds`, filtersStateSlice)
								),
							keyAccessor: (supplierFacility) => supplierFacility.id,
							valueAccessor: (supplierFacility) => supplierFacility.id,
							handleChange: (ids) => {
								updateFilters({
									supplierFacilityIds: ids.join(',') || undefined,
								});
							},
							targetCollectionName: 'workOrdersAdvancedFilters',
							fetch: fetchSupplierFacilitiesInPrivateNetwork,
							fetchMultiple: () => {},
							renderItem: (supplierFacility) => supplierFacility.name,
							sortBy: { sort_by: 'name', order: 'ascend' },
							component: OWAsyncTreeSelect,
						},
				  ]),
			{
				label: 'Resolution Type',
				fieldName: FILTER_CONFIG_NAMES.RESOULTION_TYPE,
				mode: 'multiple',
				stateSlice: resolutionTypes,
				filtersValueAccessor: (filtersStateSlice) =>
					changeFilterValueToArrayOfIds(
						nullSafeGet(FILTER_CONFIG_NAMES.RESOULTION_TYPE, filtersStateSlice)
					),
				keyAccessor: (rt) => rt.id,
				valueAccessor: (rt) => rt.id,
				handleChange: (ids) => {
					updateFilters({
						[FILTER_CONFIG_NAMES.RESOULTION_TYPE]: ids.join(',') || undefined,
					});
				},
				targetCollectionName: 'workOrdersAdvancedFilters',
				fetch: fetchResolutionTypes,
				fetchMultiple: fetchMultipleResolutionTypes,
				renderRecord: (rt) => (
					<div className="flex flex-row items-center">
						{rt.resolutionCode ? `${rt.resolutionCode} - ` : null}
						{rt.resolutionType}
					</div>
				),
				labelAccessor: (rt) => (
					<div className="flex flex-row items-center">
						{rt.resolutionCode ? `${rt.resolutionCode} - ` : null}
						{rt.resolutionType}
					</div>
				),
				sortBy: { sort_by: 'name', order: 'ascend' },
				component: OWAsyncTreeSelect,
			},
			{
				label: 'Created Between',
				labelPrefix: 'Created',
				fieldName: FILTER_CONFIG_NAMES.CREATED_AT,
				type: FILTER_FIELD_TYPE.DATE_RANGE,
				filtersValueAccessor: (filtersStateSlice) => {
					const startDate = decodeUTCAdjustedDate(
						filtersStateSlice[getStartDateFieldName(FILTER_CONFIG_NAMES.CREATED_AT)]
					);
					const endDate = decodeUTCAdjustedDate(
						filtersStateSlice[getEndDateFieldName(FILTER_CONFIG_NAMES.CREATED_AT)]
					);
					return [startDate && moment(startDate), endDate && moment(endDate)];
				},
				handleChange: (values) => {
					updateFilters({
						[getStartDateFieldName(FILTER_CONFIG_NAMES.CREATED_AT)]:
							values && values.length > 1 && values[0]
								? formatDateForParam(encodeUTCAdjustedDate(values[0]))
								: undefined,
						[getEndDateFieldName(FILTER_CONFIG_NAMES.CREATED_AT)]:
							values && values.length > 1 && values[1]
								? formatDateForParam(encodeUTCAdjustedDate(values[1], true))
								: undefined,
					});
				},
				renderRecord: (item) => item,
				valueAccessor: (item) => item,
				component: OWDateRangePicker,
			},
			{
				label: 'Completed Between',
				labelPrefix: 'Completed',
				fieldName: FILTER_CONFIG_NAMES.COMPLETED_AT,
				type: FILTER_FIELD_TYPE.DATE_RANGE,
				filtersValueAccessor: (filtersStateSlice) => {
					const startDate = decodeUTCAdjustedDate(
						filtersStateSlice[getStartDateFieldName(FILTER_CONFIG_NAMES.COMPLETED_AT)]
					);
					const endDate = decodeUTCAdjustedDate(
						filtersStateSlice[getEndDateFieldName(FILTER_CONFIG_NAMES.COMPLETED_AT)]
					);
					return [startDate && moment(startDate), endDate && moment(endDate)];
				},
				handleChange: (values) => {
					updateFilters({
						[getStartDateFieldName(FILTER_CONFIG_NAMES.COMPLETED_AT)]:
							values && values.length > 1 && values[0]
								? formatDateForParam(encodeUTCAdjustedDate(values[0]))
								: undefined,
						[getEndDateFieldName(FILTER_CONFIG_NAMES.COMPLETED_AT)]:
							values && values.length > 1 && values[1]
								? formatDateForParam(encodeUTCAdjustedDate(values[1], true))
								: undefined,
					});
				},
				renderRecord: (item) => item,
				valueAccessor: (item) => item,
				component: OWDateRangePicker,
			},
			{
				label: 'Scheduled Between',
				labelPrefix: 'Scheduled',
				fieldName: FILTER_CONFIG_NAMES.LAST_SERVICE_CALL_SCHEDULED_AT,
				type: FILTER_FIELD_TYPE.DATE_RANGE,
				filtersValueAccessor: (filtersStateSlice) => {
					const startDate = decodeUTCAdjustedDate(
						filtersStateSlice[
							getStartDateFieldName(FILTER_CONFIG_NAMES.LAST_SERVICE_CALL_SCHEDULED_AT)
						]
					);
					const endDate = decodeUTCAdjustedDate(
						filtersStateSlice[
							getEndDateFieldName(FILTER_CONFIG_NAMES.LAST_SERVICE_CALL_SCHEDULED_AT)
						]
					);
					return [startDate && moment(startDate), endDate && moment(endDate)];
				},
				handleChange: (values) => {
					updateFilters({
						[getStartDateFieldName(FILTER_CONFIG_NAMES.LAST_SERVICE_CALL_SCHEDULED_AT)]:
							values && values.length > 1 && values[0]
								? formatDateForParam(encodeUTCAdjustedDate(values[0]))
								: undefined,
						[getEndDateFieldName(FILTER_CONFIG_NAMES.LAST_SERVICE_CALL_SCHEDULED_AT)]:
							values && values.length > 1 && values[1]
								? formatDateForParam(encodeUTCAdjustedDate(values[1], true))
								: undefined,
					});
				},
				renderRecord: (item) => item,
				valueAccessor: (item) => item,
				component: OWDateRangePicker,
			},
			{
				fieldName: 'isStale',
				type: FILTER_FIELD_TYPE.RADIO_GROUP,
				items: [
					{
						label: 'All',
						value: undefined,
					},
					{
						label: 'Fresh',
						value: 'false',
					},
					{
						label: 'Stale',
						value: 'true',
					},
				],
				filtersValueAccessor: (filtersStateSlice) => {
					const val = filtersStateSlice.isStale;
					return typeof val === 'undefined' ? undefined : `${val}`;
				},
				keyAccessor: (item) => item.value,
				valueAccessor: (item) => item.value,
				handleChange: (isStale) => {
					const val = isStale ? isStale === 'true' : undefined;
					updateFilters({ isStale: val });
				},

				renderRecord: (item) => item.label,
				component: OWRadioGroup,
				info: [
					{
						title: 'Fresh',
						description: `Work orders less than ${staleDays} days old.`,
					},
					{
						title: 'Stale',
						description: `Work orders older than ${staleDays} days.`,
					},
				],
			},
			{
				fieldName: 'isPM',
				type: 'radioGroup',
				items: [
					{
						label: 'All',
						value: undefined,
					},
					{
						label: 'Planned',
						value: 'true',
					},
					{
						label: 'Reactive',
						value: 'false',
					},
				],
				filtersValueAccessor: (filtersStateSlice) => {
					const val = filtersStateSlice.isPM;
					return typeof val === 'undefined' ? undefined : `${val}`;
				},
				keyAccessor: (item) => item.value,
				valueAccessor: (item) => item.value,
				handleChange: (isPM) => {
					const val = isPM ? isPM === 'true' : undefined;
					updateFilters({ isPM: val });
				},
				renderRecord: (item) => item.label,
				component: OWRadioGroup,
				info: [
					{
						title: 'Planned',
						description: `Scheduled maintenance work generated based on planned maintenance system.`,
					},
					{
						title: 'Reactive',
						description: `On demand maintenance work requested by your team or suggested by a service provider.`,
					},
				],
			},
			{
				fieldName: 'isWarranty',
				type: 'radioGroup',
				items: [
					{
						label: 'All',
						value: 'undefined',
					},
					{
						label: 'Under Warranty',
						value: 'true',
					},
				],
				filtersValueAccessor: (filtersStateSlice) => {
					return typeof filtersStateSlice.isWarranty === 'undefined'
						? 'undefined'
						: `${filtersStateSlice.isWarranty}`;
				},
				defaultValue: undefined,
				keyAccessor: (item) => item.value,
				valueAccessor: (item) => item.value,
				handleChange: (isWarranty) => {
					const val = isWarranty === 'undefined' ? undefined : true;
					updateFilters({ isWarranty: val });
				},
				renderRecord: (item) => item.label,
				component: OWRadioGroup,
				info: [
					{
						title: 'Under Warranty',
						description: `Work Orders for assets that are under warranty`,
					},
				],
			},
			{
				fieldName: 'pastDue',
				type: 'radioGroup',
				items: [
					{
						label: 'All',
						value: 'undefined',
					},
					{
						label: 'Past Due',
						value: 'true',
					},
				],
				filtersValueAccessor: (filtersStateSlice) => {
					return typeof filtersStateSlice.pastDue === 'undefined'
						? 'undefined'
						: `${filtersStateSlice.pastDue}`;
				},
				defaultValue: undefined,
				keyAccessor: (item) => item.value,
				valueAccessor: (item) => item.value,
				handleChange: (pastDue) => {
					const val = pastDue === 'undefined' ? undefined : true;
					updateFilters({ pastDue: val });
				},
				renderRecord: (item) => item.label,
				component: OWRadioGroup,
				info: [
					{
						title: 'Past Due',
						description: `Work Orders which are past their due date`,
					},
				],
			},
			{
				fieldName: 'pastSchedule',
				type: 'radioGroup',
				items: [
					{
						label: 'All',
						value: 'undefined',
					},
					{
						label: 'Past Schedule',
						value: 'true',
					},
				],
				filtersValueAccessor: (filtersStateSlice) => {
					return typeof filtersStateSlice.pastSchedule === 'undefined'
						? 'undefined'
						: `${filtersStateSlice.pastSchedule}`;
				},
				defaultValue: undefined,
				keyAccessor: (item) => item.value,
				valueAccessor: (item) => item.value,
				handleChange: (pastSchedule) => {
					const val = pastSchedule === 'undefined' ? undefined : true;
					updateFilters({ pastSchedule: val });
				},
				renderRecord: (item) => item.label,
				component: OWRadioGroup,
				info: [
					{
						title: 'Past Schedule',
						description: `Work Orders which are past their schedule`,
					},
				],
			},
			{
				fieldName: FILTER_CONFIG_NAMES.IS_PAST_ESTIMATED_COMPLETION,
				type: 'radioGroup',
				items: [
					{
						label: 'All',
						value: 'undefined',
					},
					{
						label: 'Past Estimated Completion',
						value: 'true',
					},
				],
				filtersValueAccessor: (filtersStateSlice) => {
					return typeof filtersStateSlice.pastEstimatedCompletion === 'undefined'
						? 'undefined'
						: `${filtersStateSlice.pastEstimatedCompletion}`;
				},
				defaultValue: undefined,
				keyAccessor: (item) => item.value,
				valueAccessor: (item) => item.value,
				handleChange: (pastEstimatedCompletion) => {
					const val = pastEstimatedCompletion === 'undefined' ? undefined : true;
					updateFilters({ pastEstimatedCompletion: val });
				},
				renderRecord: (item) => item.label,
				component: OWRadioGroup,
				info: [
					{
						title: 'Past Estimated Completion',
						description: `Work Orders which are past their estimated completion date`,
					},
				],
			},
			{
				fieldName: FILTER_CONFIG_NAMES.IS_ASSET_DOWN,
				type: 'radioGroup',
				items: [
					{
						label: 'All',
						value: undefined,
					},
					{
						label: 'Asset Reported Offline',
						value: 'true',
					},
				],
				filtersValueAccessor: (filtersStateSlice) =>
					typeof filtersStateSlice.isAssetDown === 'undefined'
						? undefined
						: `${filtersStateSlice.isAssetDown}`,
				keyAccessor: (item) => item.value,
				valueAccessor: (item) => item.value,
				handleChange: (isAssetDown) =>
					updateFilters({
						isAssetDown: isAssetDown ? isAssetDown === 'true' : undefined,
					}),
				renderRecord: (item) => item.label,
				component: OWRadioGroup,
				info: [
					{
						title: 'Asset Reported as Offline',
						description: 'Asset was reported offline during work order creation.',
					},
				],
			},
			{
				label: 'Created by',
				fieldName: FILTER_CONFIG_NAMES.CREATED_BY,
				mode: 'multiple',
				stateSlice: users,
				valueType: FILTER_VALUE_TYPE.STRING,
				filtersValueAccessor: (filtersStateSlice) =>
					nullSafeGet(FILTER_CONFIG_NAMES.CREATED_BY, filtersStateSlice)
						? Array.isArray(nullSafeGet(FILTER_CONFIG_NAMES.CREATED_BY, filtersStateSlice))
							? nullSafeGet(FILTER_CONFIG_NAMES.CREATED_BY, filtersStateSlice)
							: nullSafeGetOrElse(FILTER_CONFIG_NAMES.CREATED_BY, filtersStateSlice, '').split(',')
						: undefined,
				keyAccessor: (rt) => rt.id,
				valueAccessor: (rt) => rt.id,
				handleChange: (ids) => {
					updateFilters({
						[FILTER_CONFIG_NAMES.CREATED_BY]: nullSafeGet('length', ids) ? ids : undefined,
					});
				},
				targetCollectionName: 'workOrdersAdvancedFilters',
				fetch: fetchWithDifferntSearchName(fetchUsers, 'search'),
				fetchMultiple: fetchMultipleUsers,
				renderItem: (ft) => (
					<UserName
						mode="dropdown"
						allowHover={false}
						contact={nullSafeGet('contact', ft)}
						account={nullSafeGet('account', ft)}
					/>
				),
				labelAccessor: (ft) => getContactName(nullSafeGet('contact', ft)),
				renderSelectedText: (user) => getContactName(nullSafeGet('contact', user)),
				sortBy: { sort_by: 'name', order: 'ascend' },
				component: OWAsyncSelect,
			},
			{
				label: 'Notes Status',
				fieldName: FILTER_CONFIG_NAMES.NOTES_STATUS,
				valueType: FILTER_VALUE_TYPE.STRING,
				type: FILTER_FIELD_TYPE.RADIO_GROUP,
				filtersValueAccessor: (filtersStateSlice) =>
					nullSafeGet(FILTER_CONFIG_NAMES.NOTES_STATUS, filtersStateSlice),
				handleChange: (notesStatus) => {
					updateFilters({ [FILTER_CONFIG_NAMES.NOTES_STATUS]: notesStatus });
				},
				valueAccessor: (item) => item.value,
				renderRecord: (item) => <span>{item.label}</span>,
				keyAccessor: (item) => item.value,
				items: [
					{
						label: 'Unread Notes',
						value: 'unread',
					},
					{
						label: 'Waiting on you',
						value: 'awaitingMyResponse',
					},
					{
						label: "Awaiting team's response",
						value: 'awaitingResponse',
					},
					{
						label: 'Pending response [>7d]',
						value: 'awaitingResponseStale',
					},
				],
				component: OWSelect,
			},
		],
		[
			locations,
			fetchLocations,
			areas,
			fetchAreasDistinct,
			workOrderPriorities,
			fetchWorkOrderPriorities,
			assets,
			fetchAssets,
			fetchMultipleAssets,
			assetTypes,
			fetchAssetTypes,
			fetchMultipleAssetTypes,
			planned_maintenance_schedules,
			fetchPMSchedule,
			fetchMultiplePMSchedule,
			problemTypes,
			fetchProblemTypes,
			currentUser,
			supplierFacilities,
			fetchSupplierFacilitiesInPrivateNetwork,
			resolutionTypes,
			fetchResolutionTypes,
			fetchMultipleResolutionTypes,
			staleDays,
			updateFilters,
			users,
			fetchUsers,
			fetchMultipleUsers,
		]
	);
};
